<template>
  <div v-if="wifi" class="customer-wifi-access">
    <button
      class="customer-wifi-access__trigger"
      type="button"
      aria-label="Abrir QR Code da rede Wi-Fi do totem"
      @click="openWifi"
    >
      <span class="customer-wifi-access__symbol" aria-hidden="true">
        <span class="customer-wifi-access__circle">
          <v-icon
            color="white"
            size="clamp(52px, 10.5vw, 112px)"
            class="customer-wifi-access__wifi-icon"
          >
            mdi-wifi
          </v-icon>
        </span>
      </span>
      <span class="customer-wifi-access__copy">
        <strong>Sem internet no celular?</strong>
        <span class="customer-wifi-access__subtitle">
          Toque aqui para conectar no Wi-Fi
        </span>
        <span class="customer-wifi-access__hint">
          Ao tocar, abriremos o QR Code da rede.
        </span>
      </span>
      <svg
        class="customer-wifi-access__arrow"
        viewBox="0 0 24 40"
        aria-hidden="true"
      >
        <path
          d="M4 4L20 20L4 36"
          fill="none"
          stroke="currentColor"
          stroke-width="4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <v-dialog v-model="wifiDialog" max-width="620" persistent>
      <v-card class="wifi-dialog">
        <button
          class="wifi-dialog__close"
          type="button"
          aria-label="Fechar acesso à rede Wi-Fi"
          @click="wifiDialog = false"
        >
          <v-icon size="38">mdi-close</v-icon>
        </button>
        <div class="wifi-dialog__icon">
          <v-icon color="white" size="52">mdi-wifi</v-icon>
        </div>
        <h2>Internet para continuar</h2>
        <p>Aponte a câmera do celular para conectar à rede.</p>
        <div class="wifi-dialog__qr">
          <img
            v-if="wifiQrCode"
            :src="wifiQrCode"
            alt="QR Code para conectar à rede Wi-Fi"
          />
          <p v-else-if="wifiQrError" role="alert">
            Não foi possível gerar o QR desta rede.
          </p>
          <v-progress-circular v-else indeterminate color="#536dfe" />
        </div>
        <div class="wifi-dialog__network">
          <span>Rede</span>
          <strong>{{ wifi.ssid }}</strong>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import QRCode from "qrcode";
import { fetchTotemDeviceConfig } from "@/service/totemDeviceConfig";
import qrDialogTimeout from "@/mixins/qrDialogTimeout";

export default {
  name: "CustomerWifiAccess",
  mixins: [qrDialogTimeout("wifiDialog")],
  data() {
    return {
      config: null,
      wifiDialog: false,
      wifiQrCode: "",
      wifiQrError: false,
      refreshTimer: null,
    };
  },
  computed: {
    totemId() {
      return (
        this.$store.getters["totem/getToken"] || localStorage.getItem("totemId")
      );
    },
    wifi() {
      return this.config && this.config.wifi;
    },
  },
  watch: {
    totemId(value, previousValue) {
      if (value && value !== previousValue) this.loadConfiguration();
    },
  },
  mounted() {
    this.loadConfiguration();
    this.refreshTimer = window.setInterval(
      () => this.loadConfiguration(),
      30000
    );
  },
  beforeDestroy() {
    if (this.refreshTimer) window.clearInterval(this.refreshTimer);
  },
  methods: {
    async loadConfiguration() {
      if (!this.totemId) return;
      try {
        const previousPayload = this.wifi && this.wifi.qrPayload;
        this.config = await fetchTotemDeviceConfig(this.totemId, {
          force: true,
        });
        if (!this.wifi || previousPayload !== this.wifi.qrPayload) {
          this.wifiDialog = false;
          this.wifiQrCode = "";
        }
      } catch (error) {
        // A opção de rede é complementar e não pode bloquear o envio de fotos.
        console.warn("Configuração de Wi-Fi do totem indisponível.", error);
        this.config = null;
        this.wifiDialog = false;
        this.wifiQrCode = "";
      }
    },
    async openWifi() {
      if (!this.wifi) return;
      const payload = this.wifi.qrPayload;
      const generation = this.openQrDialog();
      if (this.wifiQrCode) return;
      this.wifiQrError = false;
      try {
        const qrCode = await QRCode.toDataURL(payload, {
          errorCorrectionLevel: "M",
          margin: 1,
          width: 360,
        });
        if (
          this.isQrDialogRequestCurrent(generation) &&
          this.wifi?.qrPayload === payload
        ) {
          this.wifiQrCode = qrCode;
        }
      } catch (error) {
        if (!this.isQrDialogRequestCurrent(generation)) return;
        this.wifiQrError = true;
        console.warn("Não foi possível gerar o QR de Wi-Fi.", error);
      }
    },
  },
};
</script>

<style scoped>
.customer-wifi-access {
  margin: clamp(20px, 3.3vw, 36px) auto clamp(24px, 4.8vw, 52px);
  width: 100%;
}

.customer-wifi-access__trigger {
  align-items: center;
  animation: customer-wifi-pulse 2.4s ease-in-out infinite;
  background: linear-gradient(140deg, #ed10a5 0%, #bd0b83 55%, #8c2667 100%);
  border: 3px solid #f44cbd;
  border-radius: clamp(18px, 2.9vw, 32px);
  box-shadow: 0 10px 28px rgba(101, 25, 70, 0.24);
  box-sizing: border-box;
  color: #fff;
  column-gap: clamp(14px, 2.8vw, 30px);
  cursor: pointer;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  line-height: 1.25;
  min-height: clamp(140px, 22vw, 238px);
  padding: clamp(16px, 2.2vw, 24px);
  text-align: left;
  width: 100%;
}

.customer-wifi-access__trigger:focus-visible {
  outline: 3px solid #fff;
  outline-offset: 4px;
}

.customer-wifi-access__symbol {
  border-right: 3px solid rgba(255, 255, 255, 0.28);
  padding-right: clamp(14px, 2.5vw, 26px);
}

.customer-wifi-access__circle {
  align-items: center;
  background: linear-gradient(140deg, #ff2cbb, #e60a9c);
  border-radius: 50%;
  display: flex;
  height: clamp(80px, 15.1vw, 162px);
  justify-content: center;
  width: clamp(80px, 15.1vw, 162px);
}

.customer-wifi-access__wifi-icon {
  font-size: clamp(52px, 10.5vw, 112px);
}

.customer-wifi-access__copy {
  display: grid;
  gap: clamp(4px, 0.6vw, 7px);
  min-width: 0;
}

.customer-wifi-access__copy strong {
  display: block;
  font-size: clamp(24px, 4.3vw, 46px);
  font-weight: 700;
}

.customer-wifi-access__subtitle {
  font-size: clamp(19px, 3.5vw, 38px);
  font-weight: 500;
}

.customer-wifi-access__hint {
  color: #f4c2e5;
  font-size: clamp(15px, 2.9vw, 31px);
  font-weight: 400;
}

.customer-wifi-access__arrow {
  display: block;
  height: auto;
  width: clamp(24px, 3.8vw, 41px);
}

@keyframes customer-wifi-pulse {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 10px 28px rgba(101, 25, 70, 0.24);
  }
  50% {
    transform: scale(1.015);
    box-shadow: 0 14px 36px rgba(140, 38, 103, 0.38);
  }
}

@media (min-aspect-ratio: 1/1) {
  .customer-wifi-access {
    margin: 20px auto 24px;
  }

  .customer-wifi-access__trigger {
    column-gap: 20px;
    min-height: clamp(120px, 12vh, 150px);
    padding: 16px;
  }

  .customer-wifi-access__symbol {
    padding-right: 20px;
  }

  .customer-wifi-access__circle {
    height: clamp(72px, 10vh, 108px);
    width: clamp(72px, 10vh, 108px);
  }

  .customer-wifi-access__wifi-icon {
    font-size: clamp(52px, 8vh, 96px) !important;
  }

  .customer-wifi-access__copy {
    gap: 4px;
  }

  .customer-wifi-access__copy strong {
    font-size: clamp(24px, 3vh, 34px);
  }

  .customer-wifi-access__subtitle {
    font-size: clamp(20px, 2.5vh, 28px);
  }

  .customer-wifi-access__hint {
    color: #fff;
    font-size: clamp(16px, 2vh, 22px);
  }

  .customer-wifi-access__arrow {
    width: 28px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .customer-wifi-access__trigger {
    animation: none;
  }
}

@media (max-width: 830px) {
  .customer-wifi-access__hint {
    color: #fff;
  }
}

@media (max-aspect-ratio: 1/1) and (max-height: 1000px) {
  .customer-wifi-access {
    margin: 16px auto 20px;
  }
}

.wifi-dialog {
  align-items: center;
  border-radius: 30px !important;
  color: #2c3763;
  display: flex;
  flex-direction: column;
  padding: 48px 46px 42px;
  position: relative;
  text-align: center;
}

.wifi-dialog__close {
  background: transparent;
  border: 0;
  position: absolute;
  right: 20px;
  top: 18px;
}

.wifi-dialog__icon {
  align-items: center;
  background: #536dfe;
  border-radius: 50%;
  display: flex;
  height: 84px;
  justify-content: center;
  width: 84px;
}

.wifi-dialog h2 {
  font-size: 36px;
  margin: 24px 0 10px;
}

.wifi-dialog > p {
  font-size: 22px;
  line-height: 1.45;
  margin: 0 0 24px;
  max-width: 470px;
}

.wifi-dialog__qr {
  align-items: center;
  background: #fff;
  border: 2px solid #ebeaf1;
  border-radius: 22px;
  display: flex;
  height: 380px;
  justify-content: center;
  padding: 10px;
  width: 380px;
}

.wifi-dialog__qr img {
  display: block;
  height: 360px;
  width: 360px;
}

.wifi-dialog__qr p {
  color: #a12622;
  font-size: 19px;
  margin: 0;
  max-width: 300px;
}

.wifi-dialog__network {
  background: #f6f7fb;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  font-size: 24px;
  font-weight: 700;
  gap: 4px;
  margin-top: 24px;
  padding: 16px 24px;
  width: 100%;
}

.wifi-dialog__network span {
  color: #697196;
  font-size: 16px;
  font-weight: 500;
  text-transform: uppercase;
}
</style>
